import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './AddToMealPlanModal.css';

function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatWeekLabel(monday, language) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  const locale = language === 'hu' ? 'hu-HU' : 'en-GB';
  return `${monday.toLocaleDateString(locale, opts)} – ${sunday.toLocaleDateString(locale, opts)}`;
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const DAYS = [0, 1, 2, 3, 4, 5, 6];

export default function AddToMealPlanModal({ recipeId, recipeTitle, onClose }) {
  const { language } = useLanguage();
  const t = translations[language];

  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingSlot, setPendingSlot] = useState(null); // {dow, mealType} currently saving

  const mealLabel = {
    breakfast: t.mealPlanBreakfast,
    lunch: t.mealPlanLunch,
    dinner: t.mealPlanDinner,
  };

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    const weekStr = toDateStr(weekStart);
    const { data } = await supabase
      .from('meal_plan')
      .select('id, day_of_week, meal_type, recipe_id, recipes(id, title)')
      .eq('week_start', weekStr);
    setPlan(data || []);
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  function getEntry(dow, mealType) {
    return plan.find(p => p.day_of_week === dow && p.meal_type === mealType) || null;
  }

  async function handleSlotClick(dow, mealType) {
    const key = `${dow}:${mealType}`;
    setPendingSlot(key);
    const weekStr = toDateStr(weekStart);
    const existing = getEntry(dow, mealType);

    if (existing && existing.recipe_id === recipeId) {
      // Remove — this recipe is already here
      await supabase.from('meal_plan').delete().eq('id', existing.id);
    } else {
      // Assign (replacing whatever was there)
      if (existing) {
        await supabase.from('meal_plan').delete().eq('id', existing.id);
      }
      await supabase.from('meal_plan').insert({
        week_start: weekStr,
        day_of_week: dow,
        meal_type: mealType,
        recipe_id: recipeId,
      });
    }

    await fetchPlan();
    setPendingSlot(null);
  }

  function prevWeek() {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  }

  function nextWeek() {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  }

  function dayDate(dow) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dow);
    return d;
  }

  const dayLabels = t.mealPlanDays;

  return (
    <div className="atmp-overlay" onClick={onClose}>
      <div className="atmp-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="atmp-header">
          <span className="atmp-title">{t.mealPlanModalTitle}</span>
          <button className="atmp-close" onClick={onClose}>×</button>
        </div>

        <p className="atmp-recipe-name">{recipeTitle}</p>

        {/* Week nav */}
        <div className="atmp-week-nav">
          <button className="atmp-week-btn" onClick={prevWeek}>‹</button>
          <span className="atmp-week-label">{formatWeekLabel(weekStart, language)}</span>
          <button className="atmp-week-btn" onClick={nextWeek}>›</button>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="atmp-loading">{t.loading}</p>
        ) : (
          <div className="atmp-grid">
            {DAYS.map(dow => {
              const date = dayDate(dow);
              return (
                <div key={dow} className="atmp-day-col">
                  <div className="atmp-day-header">
                    <span className="atmp-day-name">{dayLabels[dow]}</span>
                    <span className="atmp-day-num">{date.getDate()}</span>
                  </div>
                  {MEAL_TYPES.map(mealType => {
                    const entry = getEntry(dow, mealType);
                    const isThisRecipe = entry && entry.recipe_id === recipeId;
                    const isOtherRecipe = entry && entry.recipe_id !== recipeId;
                    const isPending = pendingSlot === `${dow}:${mealType}`;

                    return (
                      <button
                        key={mealType}
                        className={[
                          'atmp-slot',
                          isThisRecipe ? 'atmp-slot--mine' : '',
                          isOtherRecipe ? 'atmp-slot--other' : '',
                          isPending ? 'atmp-slot--pending' : '',
                        ].join(' ')}
                        onClick={() => !isPending && handleSlotClick(dow, mealType)}
                        disabled={isPending}
                        title={isOtherRecipe ? entry.recipes?.title : ''}
                      >
                        <span className="atmp-slot-meal">{mealLabel[mealType]}</span>
                        {isThisRecipe && <span className="atmp-slot-check">✓</span>}
                        {isOtherRecipe && (
                          <span className="atmp-slot-other-name">{entry.recipes?.title}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        <p className="atmp-hint">
          {language === 'hu'
            ? 'Koppints egy időpontra a recept hozzáadásához. A ✓ jelölt időpontra koppintva eltávolítja.'
            : 'Tap a slot to add the recipe. Tap a ✓ slot to remove it.'}
        </p>

      </div>
    </div>
  );
}

